/** Open-Meteo weather snapshot for the app header. */
export interface HeaderWeather {
  tempC: number;
  rainPct: number;
  fetchedAt: number;
}

export type RideConditionLevel = 'excellent' | 'good' | 'caution' | 'poor';

export interface HeaderWeatherDetail extends HeaderWeather {
  feelsLikeC: number;
  windKmh: number;
  humidityPct: number;
  sunrise: string;
  sunset: string;
  rideCondition: RideConditionLevel;
}

export const RIDE_CONDITION_LABELS: Record<RideConditionLevel, string> = {
  excellent: 'Excellent Riding',
  good: 'Good Riding',
  caution: 'Use Caution',
  poor: 'Poor Riding',
};

const CACHE_KEY = 'rm-header-weather-v2';
const TTL_MS = 30 * 60 * 1000;

interface WeatherCacheEntry extends HeaderWeatherDetail {
  lat: number;
  lon: number;
}

function readCache(): WeatherCacheEntry | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as WeatherCacheEntry;
  } catch {
    return null;
  }
}

function writeCache(entry: WeatherCacheEntry): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    /* quota / private mode */
  }
}

function coordsClose(a: number, b: number, epsilon = 0.08): boolean {
  return Math.abs(a - b) <= epsilon;
}

function currentHourIndex(): number {
  return new Date().getHours();
}

function formatLocalTime(iso: string | undefined): string {
  if (!iso) return '-';
  try {
    return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(
      new Date(iso),
    );
  } catch {
    return '-';
  }
}

/** Derive motorcycle ride suitability from current conditions. */
export function deriveRideCondition(
  rainPct: number,
  windKmh: number,
  tempC: number,
): RideConditionLevel {
  if (rainPct > 60 || windKmh > 60 || tempC < 0 || tempC > 42) return 'poor';
  if (rainPct >= 30 || windKmh >= 45 || tempC < 5 || tempC > 38) return 'caution';
  if (rainPct < 15 && windKmh < 30 && tempC >= 15 && tempC <= 35) return 'excellent';
  if (rainPct < 30 && windKmh < 45) return 'good';
  return 'caution';
}

/** Emoji hint from rain probability (display only). */
export function weatherEmoji(rainPct: number, tempC: number): string {
  if (rainPct >= 80) return '🌩';
  if (rainPct >= 40) return '🌦';
  if (rainPct >= 15) return '🌧';
  if (tempC >= 28) return '☀';
  return '☀';
}

/** Read cached weather if fresh and near the same coordinates. */
export function getCachedHeaderWeather(lat: number, lon: number): HeaderWeatherDetail | null {
  const cached = readCache();
  if (!cached) return null;
  if (Date.now() - cached.fetchedAt > TTL_MS) return null;
  if (!coordsClose(cached.lat, lat) || !coordsClose(cached.lon, lon)) return null;
  const { lat: _lat, lon: _lon, ...weather } = cached;
  return weather;
}

/** Read last cached weather even when GPS coords differ (fallback after permission denied). */
export function getLastCachedHeaderWeather(maxAgeMs = TTL_MS * 2): HeaderWeatherDetail | null {
  const cached = readCache();
  if (!cached) return null;
  if (Date.now() - cached.fetchedAt > maxAgeMs) return null;
  const { lat: _lat, lon: _lon, ...weather } = cached;
  return weather;
}

/** Fetch current weather from Open-Meteo (no API key). */
export async function fetchHeaderWeather(
  lat: number,
  lon: number,
): Promise<HeaderWeatherDetail | null> {
  const cached = getCachedHeaderWeather(lat, lon);
  if (cached) return cached;

  try {
    const params = new URLSearchParams({
      latitude: String(lat),
      longitude: String(lon),
      current: 'temperature_2m,apparent_temperature,relative_humidity_2m,wind_speed_10m',
      hourly: 'precipitation_probability',
      daily: 'sunrise,sunset',
      forecast_days: '1',
      wind_speed_unit: 'kmh',
      timezone: 'auto',
    });
    const res = await fetch(`https://api.open-meteo.com/v1/forecast?${params}`);
    if (!res.ok) {
      console.warn('[RideMaadi Header Weather] Open-Meteo HTTP error', res.status);
      return null;
    }
    const data = (await res.json()) as {
      current?: {
        temperature_2m?: number;
        apparent_temperature?: number;
        relative_humidity_2m?: number;
        wind_speed_10m?: number;
      };
      hourly?: { precipitation_probability?: number[] };
      daily?: { sunrise?: string[]; sunset?: string[] };
    };
    const tempC = data.current?.temperature_2m;
    if (tempC == null || !Number.isFinite(tempC)) return null;

    const hourIdx = currentHourIndex();
    const rainPct = Math.round(data.hourly?.precipitation_probability?.[hourIdx] ?? 0);
    const feelsLikeC = Math.round(data.current?.apparent_temperature ?? tempC);
    const windKmh = Math.round(data.current?.wind_speed_10m ?? 0);
    const humidityPct = Math.round(data.current?.relative_humidity_2m ?? 0);
    const sunrise = formatLocalTime(data.daily?.sunrise?.[0]);
    const sunset = formatLocalTime(data.daily?.sunset?.[0]);
    const roundedTemp = Math.round(tempC);

    const entry: WeatherCacheEntry = {
      lat,
      lon,
      tempC: roundedTemp,
      rainPct,
      feelsLikeC,
      windKmh,
      humidityPct,
      sunrise,
      sunset,
      rideCondition: deriveRideCondition(rainPct, windKmh, roundedTemp),
      fetchedAt: Date.now(),
    };
    writeCache(entry);
    return entry;
  } catch (err) {
    console.warn('[RideMaadi Header Weather] Open-Meteo request failed', err);
    return null;
  }
}

export function clearHeaderWeatherCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    /* ignore */
  }
}
